import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getAssetDetail, getAssetVersions } from '../../services'
import { Tabs } from 'vnetwork-ui'
import useTabs from '@/hooks/useTabs'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import AssetHeader from './AssetHeader'
import OverviewTab from './tabs/OverviewTab'
import FilesTab from './tabs/FilesTab'
import ApprovalTab from './tabs/ApprovalTab'
import ShareTab from './tabs/ShareTab'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useAssetDetailTour } from './use-asset-detail-tour'
import { useTourHandler } from '@/contexts/tour-handler'
import { useEffect } from 'react'
import { FileX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'

const AssetDetail = () => {
    const { id } = useParams<{ id: string }>() // id is actually asset_slug from URL
    const { t } = useTranslation('asset')
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const versionId = searchParams.get('version') || undefined

    // Tour
    const { startTour } = useAssetDetailTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Use React Query to fetch asset detail for proper cache management
    const {
        data: asset,
        isLoading,
        error,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ASSET_DETAIL, id, versionId],
        queryFn: async () => {
            if (!id) return null
            const response = await getAssetDetail(id, versionId)
            return response.result
        },
        enabled: !!id,
    })

    // Fetch versions list
    const { data: versionsData } = useQueryWithHandler({
        queryKey: ['asset-versions', asset?.asset_id],
        queryFn: async () => {
            if (!asset?.asset_id) return []
            const response = await getAssetVersions(asset.asset_id)
            return response.results ?? []
        },
        enabled: !!asset?.asset_id,
    })

    console.log('versionsData', versionsData)

    const handleVersionChange = (versionId: string) => {
        setSearchParams({ version: versionId })
    }

    const { TabComponent, TabProps } = useTabs({
        tabs: [
            {
                label: t('tabs.overview'),
                value: 'overview',
                component: asset ? <OverviewTab asset={asset} /> : <></>,
            },
            {
                label: t('tabs.attachments'),
                value: 'files',
                component: asset ? (
                    <FilesTab
                        files={asset.files}
                        canDownload={asset.can_download}
                    />
                ) : (
                    <></>
                ),
            },
            {
                label: t('tabs.approval'),
                value: 'approval',
                component: asset ? <ApprovalTab asset={asset} /> : <></>,
            },
            // {
            //     label: t('tabs.history'),
            //     value: 'history',
            //     component: asset ? (
            //         <HistoryTab assetSlug={asset.asset_slug} />
            //     ) : (
            //         <></>
            //     ),
            // },
            {
                label: t('tabs.share'),
                value: 'share',
                component: asset ? <ShareTab asset={asset} /> : <></>,
            },
        ],
    })

    if (isLoading) {
        return (
            <div className="p-6 space-y-6">
                <div className="h-32 bg-gray-100 rounded-lg animate-pulse" />
                <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
            </div>
        )
    }

    if (error || !asset) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
                <FileX className="w-16 h-16 text-gray-300" />
                <div className="text-center space-y-1">
                    <p className="text-lg font-medium text-gray-700">
                        {t('assetNotFound')}
                    </p>
                    <p className="text-sm text-gray-400">
                        {t('assetNotFoundDescription')}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate(paths.assetsList)}
                >
                    {t('backToList')}
                </Button>
            </div>
        )
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.assetsList,
                        title: t('assetList'),
                    },
                    {
                        title: asset.asset_name,
                        path: '', // Active item doesn't need navigation
                    },
                ]}
            />
            <div className="flex flex-col h-full overflow-auto">
                <AssetHeader
                    asset={asset}
                    versions={versionsData ?? undefined}
                    selectedVersionId={
                        versionId || asset.current_version.asset_version_id
                    }
                    onVersionChange={handleVersionChange}
                />

                <div data-tour="asset-detail-tabs">
                    <Tabs {...TabProps} />
                </div>
                <div className="mt-2">{TabComponent}</div>
            </div>
        </div>
    )
}

export default AssetDetail
